import React from 'react';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Typography,
} from '@mui/material';
import { DIAMOND_TYPES } from '../../constants/appConstants';
import {
  getItemDetails,
  getItemRef,
  getItemStatusLabel,
  getItemTypeLabel,
} from '../../utils/orderItemDisplay';
import { buttonStartIcon } from '../../utils/buttonLoading';

/**
 * Bulk cancel confirmation with per-line return-to-inventory toggles.
 */
export default function BulkCancelOrderItemsDialog({
  open,
  onClose,
  items,
  onItemsChange,
  onConfirm,
  loading = false,
  isMobile = false,
  t,
  titleKey = 'orders.cancelItem',
  bodyKey = 'orders.cancelItemBody',
  confirmKey = 'checks.cancelSelectedItems',
}) {
  const returnEligible = items.filter((item) => item.type !== DIAMOND_TYPES.CUSTOM);
  const allReturnChecked = returnEligible.length > 0
    && returnEligible.every((item) => item.returnToInventory);
  const someReturnChecked = returnEligible.some((item) => item.returnToInventory);

  const toggleAllReturn = (checked) => {
    onItemsChange(items.map((item) => (
      item.type !== DIAMOND_TYPES.CUSTOM ? { ...item, returnToInventory: checked } : item
    )));
  };

  const handleClose = () => {
    if (loading) return;
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      disableEscapeKeyDown={loading}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h6" color="error.main">
          {items.length > 1 ? t('checks.cancelSelectedItems', { count: items.length }) : t(titleKey)}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {t(bodyKey)}
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        {returnEligible.length > 1 && (
          <Box mb={1}>
            <FormControlLabel
              control={(
                <Checkbox
                  checked={allReturnChecked}
                  indeterminate={someReturnChecked && !allReturnChecked}
                  onChange={(e) => toggleAllReturn(e.target.checked)}
                />
              )}
              label={(
                <Typography variant="body2" fontWeight="bold">
                  {t('orders.selectDeselectAll')}
                </Typography>
              )}
            />
            <Divider />
          </Box>
        )}
        {items.map((item) => (
          <Box key={item._id} display="flex" alignItems="center" justifyContent="space-between" sx={{ py: 1 }}>
            <Box flex={1}>
              <Typography variant="body2" fontWeight="bold">
                {getItemTypeLabel(item, t)} — {getItemRef(item)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {getItemDetails(item, t)} | {getItemStatusLabel(item.status, t)}
              </Typography>
            </Box>
            {item.type !== DIAMOND_TYPES.CUSTOM && (
              <FormControlLabel
                control={(
                  <Checkbox
                    checked={item.returnToInventory}
                    onChange={() => onItemsChange(items.map((i) => (
                      i._id === item._id ? { ...i, returnToInventory: !i.returnToInventory } : i
                    )))}
                    color="primary"
                  />
                )}
                label={<Typography variant="body2">{t('orders.returnToInventory')}</Typography>}
                sx={{ ml: 2 }}
              />
            )}
          </Box>
        ))}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} disabled={loading}>
          {t('orders.goBack')}
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={onConfirm}
          disabled={loading || items.length === 0}
          startIcon={buttonStartIcon(loading, null)}
        >
          {loading ? t('orders.cancelling') : t(confirmKey, { count: items.length })}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
